/**
 * Graph Clustering Utilities
 *
 * Provides algorithms for clustering nodes in the knowledge graph to improve
 * performance and visualization clarity when dealing with large graphs (2000+ nodes).
 *
 * Clustering strategies:
 * - By Conversation: Group messages from the same conversation
 * - By Topic: Group nodes related to the same topic
 * - By Time Period: Group nodes created in the same time range
 * - By Node Type: Group nodes of the same type
 */

import { GraphNode } from '../types/graph.types';

export enum ClusterStrategy {
  CONVERSATION = 'conversation',
  TOPIC = 'topic',
  TIME_PERIOD = 'time_period',
  NODE_TYPE = 'node_type',
  NONE = 'none',
}

export interface Cluster {
  id: string;
  label: string;
  nodeIds: string[];
  strategy: ClusterStrategy;
  metadata: {
    color?: string;
    size?: number;
    expanded?: boolean;
    [key: string]: any;
  };
}

/**
 * Determine if clustering should be applied based on node count
 */
export function shouldApplyClustering(nodeCount: number): boolean {
  const CLUSTERING_THRESHOLD = 100;
  return nodeCount > CLUSTERING_THRESHOLD;
}

/**
 * Cluster nodes by conversation ID
 */
export function clusterByConversation(nodes: GraphNode[]): Cluster[] {
  const conversationGroups = new Map<string, GraphNode[]>();

  // Group nodes by conversation ID
  nodes.forEach(node => {
    const conversationId = (node as any).properties?.conversationId ||
                          (node as any).properties?.conversation_id ||
                          node.metadata?.conversationId;

    if (conversationId) {
      if (!conversationGroups.has(conversationId)) {
        conversationGroups.set(conversationId, []);
      }
      conversationGroups.get(conversationId)!.push(node);
    }
  });

  // Create clusters (only for groups with 3+ nodes to reduce clutter)
  const clusters: Cluster[] = [];
  conversationGroups.forEach((groupNodes, conversationId) => {
    if (groupNodes.length >= 3) {
      clusters.push({
        id: `cluster-conversation-${conversationId}`,
        label: `Conversation (${groupNodes.length} nodes)`,
        nodeIds: groupNodes.map(n => n.id),
        strategy: ClusterStrategy.CONVERSATION,
        metadata: {
          conversationId,
          size: groupNodes.length,
          expanded: false,
          color: '#4056A6', // Royal Blue for conversations
        },
      });
    }
  });

  console.log('🔗 CLUSTERING: Conversation clusters', {
    totalNodes: nodes.length,
    clustersCreated: clusters.length,
    largestCluster: Math.max(...clusters.map(c => c.nodeIds.length), 0),
  });

  return clusters;
}

/**
 * Cluster nodes by topic
 */
export function clusterByTopic(nodes: GraphNode[]): Cluster[] {
  const topicGroups = new Map<string, GraphNode[]>();

  // Group nodes by primary topic
  nodes.forEach(node => {
    const topics = node.metadata?.topics || [];
    const primaryTopic = topics[0]; // Use first topic as primary

    if (primaryTopic) {
      const topicId = typeof primaryTopic === 'string' ? primaryTopic : primaryTopic.id;
      if (!topicGroups.has(topicId)) {
        topicGroups.set(topicId, []);
      }
      topicGroups.get(topicId)!.push(node);
    }
  });

  // Create clusters (only for groups with 5+ nodes)
  const clusters: Cluster[] = [];
  topicGroups.forEach((groupNodes, topicId) => {
    if (groupNodes.length >= 5) {
      const topicName = groupNodes[0].metadata?.topics?.[0]?.name || topicId;
      clusters.push({
        id: `cluster-topic-${topicId}`,
        label: `${topicName} (${groupNodes.length})`,
        nodeIds: groupNodes.map(n => n.id),
        strategy: ClusterStrategy.TOPIC,
        metadata: {
          topicId,
          topicName,
          size: groupNodes.length,
          expanded: false,
          color: '#E94257', // Coral Red for topics
        },
      });
    }
  });

  console.log('🔗 CLUSTERING: Topic clusters', {
    totalNodes: nodes.length,
    clustersCreated: clusters.length,
    largestCluster: Math.max(...clusters.map(c => c.nodeIds.length), 0),
  });

  return clusters;
}

/**
 * Cluster nodes by time period (day, week, or month)
 */
export function clusterByTimePeriod(
  nodes: GraphNode[],
  period: 'day' | 'week' | 'month' = 'day'
): Cluster[] {
  const periodGroups = new Map<string, GraphNode[]>();

  // Group nodes by time period
  nodes.forEach(node => {
    const date = new Date(node.createdAt);
    let periodKey: string;

    switch (period) {
      case 'day':
        periodKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
        break;
      case 'week':
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        periodKey = weekStart.toISOString().split('T')[0];
        break;
      case 'month':
        periodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        break;
    }

    if (!periodGroups.has(periodKey)) {
      periodGroups.set(periodKey, []);
    }
    periodGroups.get(periodKey)!.push(node);
  });

  // Create clusters (only for groups with 10+ nodes)
  const clusters: Cluster[] = [];
  periodGroups.forEach((groupNodes, periodKey) => {
    if (groupNodes.length >= 10) {
      clusters.push({
        id: `cluster-time-${periodKey}`,
        label: `${periodKey} (${groupNodes.length})`,
        nodeIds: groupNodes.map(n => n.id),
        strategy: ClusterStrategy.TIME_PERIOD,
        metadata: {
          period: periodKey,
          size: groupNodes.length,
          expanded: false,
          color: '#4DC4D2', // Turquoise for time periods
        },
      });
    }
  });

  console.log('🔗 CLUSTERING: Time period clusters', {
    totalNodes: nodes.length,
    period,
    clustersCreated: clusters.length,
    largestCluster: Math.max(...clusters.map(c => c.nodeIds.length), 0),
  });

  return clusters;
}

/**
 * Cluster nodes by type
 */
export function clusterByNodeType(nodes: GraphNode[]): Cluster[] {
  const typeGroups = new Map<string, GraphNode[]>();

  // Group nodes by type
  nodes.forEach(node => {
    if (!typeGroups.has(node.type)) {
      typeGroups.set(node.type, []);
    }
    typeGroups.get(node.type)!.push(node);
  });

  // Create clusters (only for groups with 20+ nodes)
  const clusters: Cluster[] = [];
  const typeColors: Record<string, string> = {
    message: '#4056A6',
    document: '#325BA8',
    concept: '#4DC4D2',
    person: '#892987',
    topic: '#E94257',
    organization: '#999999',
  };

  typeGroups.forEach((groupNodes, type) => {
    if (groupNodes.length >= 20) {
      clusters.push({
        id: `cluster-type-${type}`,
        label: `${type} (${groupNodes.length})`,
        nodeIds: groupNodes.map(n => n.id),
        strategy: ClusterStrategy.NODE_TYPE,
        metadata: {
          nodeType: type,
          size: groupNodes.length,
          expanded: false,
          color: typeColors[type] || '#666666',
        },
      });
    }
  });

  console.log('🔗 CLUSTERING: Node type clusters', {
    totalNodes: nodes.length,
    clustersCreated: clusters.length,
    largestCluster: Math.max(...clusters.map(c => c.nodeIds.length), 0),
  });

  return clusters;
}

/**
 * Apply clustering strategy to nodes
 */
export function applyClustering(
  nodes: GraphNode[],
  strategy: ClusterStrategy = ClusterStrategy.CONVERSATION
): Cluster[] {
  if (!shouldApplyClustering(nodes.length) || strategy === ClusterStrategy.NONE) {
    return [];
  }

  switch (strategy) {
    case ClusterStrategy.CONVERSATION:
      return clusterByConversation(nodes);
    case ClusterStrategy.TOPIC:
      return clusterByTopic(nodes);
    case ClusterStrategy.TIME_PERIOD:
      return clusterByTimePeriod(nodes);
    case ClusterStrategy.NODE_TYPE:
      return clusterByNodeType(nodes);
    default:
      return [];
  }
}

/**
 * Get nodes that are not part of any cluster
 */
export function getUnclusteredNodes(nodes: GraphNode[], clusters: Cluster[]): GraphNode[] {
  const clusteredNodeIds = new Set<string>();
  clusters.forEach(cluster => {
    cluster.nodeIds.forEach(id => clusteredNodeIds.add(id));
  });

  return nodes.filter(node => !clusteredNodeIds.has(node.id));
}

/**
 * Calculate cluster statistics
 */
export function getClusterStats(clusters: Cluster[]) {
  const totalClustered = clusters.reduce((sum, c) => sum + c.nodeIds.length, 0);
  const averageSize = clusters.length > 0 ? totalClustered / clusters.length : 0;
  const largestCluster = Math.max(...clusters.map(c => c.nodeIds.length), 0);
  const smallestCluster = Math.min(...clusters.map(c => c.nodeIds.length), Infinity);

  return {
    clusterCount: clusters.length,
    totalClustered,
    averageSize: Math.round(averageSize * 10) / 10,
    largestCluster,
    smallestCluster: smallestCluster === Infinity ? 0 : smallestCluster,
  };
}
